import { BlogDataResponse } from "@/components/Admin/Blog/interface";
import { BlogLayout } from "@/components/Blog/BlogLayout";
import { isTagExtended, useTags } from "@/hooks/useTags";
import { TAGS_KEY } from "@/lib/queryKeys";
import { getCurrentUrl } from "@/utils/getCurrentUrl";
import {
  getFirestoreBlogBySlug,
  getFirestoreBlogs,
} from "@/utils/getFirestoreBlogs";
import { getFirestoreTags } from "@/utils/getFirestoreTags";
import { getReadTime } from "@/utils/getReadTime";
import {
  SidebarMetaInfo,
  getSidebarMetaInfo,
} from "@/utils/getSidebarMetaInfo";
import { has } from "@/utils/has";
import { renderJSON } from "@/utils/renderJSON";
import { Box, Group, Text, Title, useMantineColorScheme } from "@mantine/core";
import { IconClockHour4 } from "@tabler/icons-react";
import { QueryClient, dehydrate } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import { NextSeo } from "next-seo";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import * as React from "react";
import css from "./blog.module.css";

dayjs.locale("ru");

const DynamicDiscussionEmbed = dynamic(
  () => import("disqus-react").then((mod) => mod.DiscussionEmbed),
  { ssr: false }
);

export default function ArticlePage({
  blog: {
    description,
    id,
    images,
    slug,
    tags,
    title,
    json,
    showComments,
    showUpdatedDate,
    created,
    updated,
    author,
  },
  sidebarMetaInfo,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter();

  const commentsRef = React.useRef<HTMLDivElement>(null);

  const { colorScheme } = useMantineColorScheme();

  const { tags: tagsWithInfo } = useTags(tags);

  React.useEffect(() => {
    if (has(router.query, "comments")) {
      commentsRef.current?.scrollIntoView({
        behavior: "auto",
        block: "end",
        inline: "nearest",
      });
    }
  }, [router.query]);

  const readTime = getReadTime(json);

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        openGraph={{
          description: description,
          title,
          article: {
            publishedTime: new Date(created).toISOString(),
            modifiedTime: new Date(updated).toISOString(),
            tags: tagsWithInfo.map((item) => item.label),
            authors: [author ?? ""],
          },
          images: images?.map((i) => ({
            url: i.url,
            alt: i.description,
            height: i.height,
            width: i.width,
          })),
        }}
      />
      <BlogLayout
        breadcrumb={{ href: `/blog/${slug}`, title: title ?? "" }}
        sidebarMetaInfo={sidebarMetaInfo}
      >
        <Group fz="xs" className={css.postHeader}>
          <Text fw={500} title="Автор">
            {author}
          </Text>
          <Text title="Дата создания">
            {dayjs(created).format("D MMM в HH:mm")}
          </Text>
          <Group gap="5px" title="Время чтения">
            <IconClockHour4 stroke={1} size={18} />
            <Text>{readTime} мин</Text>
          </Group>
          {tagsWithInfo.map((tag, index) =>
            isTagExtended(tag) ? (
              <Link href={`/tag/${tag.value}`} key={index} prefetch={false}>
                <Text
                  size="13px"
                  color={
                    has(tag, "color") && typeof tag.color === "string"
                      ? tag.color
                      : void 0
                  }
                >
                  #{tag.label.toUpperCase()}
                </Text>
              </Link>
            ) : (
              <Text size="13px" key={index}>
                #{tag.label.toUpperCase()}
              </Text>
            )
          )}
        </Group>

        <Title order={1} ta="center" mb="xs">
          {title}
        </Title>
        {json && <article className={css.article}>{renderJSON(json)}</article>}

        {showUpdatedDate && updated !== created && (
          <Group justify="right">
            <Text fs="italic" color="gray" fz="xs">
              изменен {dayjs(updated).format("D MMM в HH:mm")}
            </Text>
          </Group>
        )}
        {showComments && (
          <Box
            ref={commentsRef}
            pt={50}
            mih={400}
            className={css.commentsBlock}
          >
            <DynamicDiscussionEmbed
              key={colorScheme}
              shortname="wofm-art"
              config={{
                url: `${getCurrentUrl()}/blog/${slug}`,
                identifier: id,
                title,
              }}
            />
          </Box>
        )}
      </BlogLayout>
    </>
  );
}

export const getStaticProps: GetStaticProps<{
  blog: BlogDataResponse;
  sidebarMetaInfo: SidebarMetaInfo;
}> = async ({ params }) => {
  const slug = params?.slug;

  const blog = await getFirestoreBlogBySlug(
    Array.isArray(slug) ? slug[0] : slug
  );

  if (!blog) {
    return {
      notFound: true,
    };
  }

  const sidebarMetaInfo = await getSidebarMetaInfo();

  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: [TAGS_KEY],
    queryFn: getFirestoreTags,
  });

  return {
    props: { blog, sidebarMetaInfo, dehydratedState: dehydrate(queryClient) },
  };
};

export async function getStaticPaths() {
  const blogs = await getFirestoreBlogs();

  return {
    paths: blogs.map((blog) => `/blog/${blog.slug}`),
    fallback: false,
  };
}
